import { useCallback, useRef, useState } from 'react';

type AiValues<TField extends string> = Partial<Record<TField, string | number>>;

export interface AiFilledFields<TField extends string> {
  /** Valore messo dall'IA per campo, finché la proposta è quella corrente. */
  aiValues: AiValues<TField>;
  markAiFilled: (values: AiValues<TField>) => void;
  /** L'utente ha cambiato il campo a mano: la proposta non lo sovrascrive più. */
  touch: (field: TField) => void;
  isTouched: (field: TField) => boolean;
  /** L'etichetta resta finché il valore è ancora quello proposto dall'IA. */
  isStillAiProposed: (field: TField, currentValue: string | number | undefined) => boolean;
  /** Vero se l'IA ha riempito almeno uno di questi campi e nessuno è stato cambiato dopo. */
  areStillAiProposed: (
    fields: readonly TField[],
    currentValues: Partial<Record<TField, string | number | undefined>>,
  ) => boolean;
  /**
   * La proposta non vale più (file tolto o sostituito, token rifiutato): le etichette spariscono e
   * restituisce i campi ancora uguali al valore dell'IA, che il dialog riporta al valore di partenza.
   * I campi cambiati a mano restano all'utente.
   */
  withdraw: (currentValues: Partial<Record<TField, string | number | undefined>>) => TField[];
  reset: () => void;
}

export function useAiFilledFields<TField extends string>(): AiFilledFields<TField> {
  const [aiValues, setAiValues] = useState<AiValues<TField>>({});
  const aiValuesRef = useRef<AiValues<TField>>({});
  const touchedRef = useRef<Set<TField>>(new Set());

  const storeAiValues = useCallback((values: AiValues<TField>) => {
    aiValuesRef.current = values;
    setAiValues(values);
  }, []);
  const markAiFilled = useCallback(
    (values: AiValues<TField>) => storeAiValues(values),
    [storeAiValues],
  );
  const touch = useCallback((field: TField) => {
    touchedRef.current.add(field);
  }, []);
  const isTouched = useCallback((field: TField) => touchedRef.current.has(field), []);
  const isStillAiProposed = useCallback(
    (field: TField, currentValue: string | number | undefined) =>
      aiValues[field] !== undefined && aiValues[field] === currentValue,
    [aiValues],
  );
  const areStillAiProposed = useCallback(
    (
      fields: readonly TField[],
      currentValues: Partial<Record<TField, string | number | undefined>>,
    ) => {
      const filledFields = fields.filter((field) => aiValues[field] !== undefined);
      return (
        filledFields.length > 0 &&
        filledFields.every((field) => aiValues[field] === currentValues[field])
      );
    },
    [aiValues],
  );
  const withdraw = useCallback(
    (currentValues: Partial<Record<TField, string | number | undefined>>) => {
      const proposed = aiValuesRef.current;
      const unchangedFields = (Object.keys(proposed) as TField[]).filter(
        (field) => proposed[field] !== undefined && proposed[field] === currentValues[field],
      );
      storeAiValues({});
      return unchangedFields;
    },
    [storeAiValues],
  );
  const reset = useCallback(() => {
    touchedRef.current = new Set();
    storeAiValues({});
  }, [storeAiValues]);

  return {
    aiValues,
    markAiFilled,
    touch,
    isTouched,
    isStillAiProposed,
    areStillAiProposed,
    withdraw,
    reset,
  };
}
